import API from './api';

// Project Delta API client.
//
// MONEY: every amount from this API is AED MINOR UNITS (integers, 100 per AED). Nothing
// here converts — `fmtM` below formats for display and is the ONLY place a division
// happens. No screen recomputes a derived figure: Distributable Liquidity, the nexus
// aggregates and every policy limit are computed server-side so that a figure has one
// basis of calculation wherever it appears (Charter Section 9.2).

const MINOR_PER_MILLION = 1_000_000 * 100;

// AED millions, no decimals — the house convention for this module.
export const fmtM = (minor) => {
  if (minor === null || minor === undefined) return '—';
  return Math.round(minor / MINOR_PER_MILLION).toLocaleString('en-US');
};

export const fmtPct = (share, digits = 0) =>
  share === null || share === undefined ? '—' : `${(share * 100).toFixed(digits)}%`;

const deltaService = {
  getContext: () => API.get('/api/delta/context/').then((r) => r.data),
  getNexus: (params = {}) => API.get('/api/delta/nexus/', { params }).then((r) => r.data),
  getFigures: () => API.get('/api/delta/figures/').then((r) => r.data),

  getPositions: (params = {}) => API.get('/api/delta/positions/', { params }).then((r) => r.data),
  getPosition: (id) => API.get(`/api/delta/positions/${id}/`).then((r) => r.data),
  createPosition: (body) => API.post('/api/delta/positions/', body).then((r) => r.data),
  updatePosition: (id, body) => API.patch(`/api/delta/positions/${id}/`, body).then((r) => r.data),
  transitionPosition: (id, status, note = '') =>
    API.post(`/api/delta/positions/${id}/transition/`, { status, note }).then((r) => r.data),
  addValuation: (id, body) =>
    API.post(`/api/delta/positions/${id}/valuations/`, body).then((r) => r.data),
  addEncumbrance: (id, body) =>
    API.post(`/api/delta/positions/${id}/encumbrances/`, body).then((r) => r.data),
  addCommitment: (id, body) =>
    API.post(`/api/delta/positions/${id}/commitments/`, body).then((r) => r.data),

  getPolicyRegister: () => API.get('/api/delta/policies/register/').then((r) => r.data),
  getRoadmap: () => API.get('/api/delta/roadmap/').then((r) => r.data),
  updateAction: (id, body) => API.patch(`/api/delta/actions/${id}/`, body).then((r) => r.data),

  getDecisions: (params = {}) => API.get('/api/delta/decisions/', { params }).then((r) => r.data),
  recordDecision: (id, outcome) =>
    API.post(`/api/delta/decisions/${id}/record/`, { outcome }).then((r) => r.data),

  // Keyed on reference, not id: a clause citation must survive a rebuild from seed.
  getPolicyReading: (reference) =>
    API.get(`/api/delta/policies/${encodeURIComponent(reference)}/reading/`).then((r) => r.data),
  getPolicyWorkspace: () => API.get('/api/delta/policy-workspace/').then((r) => r.data),
  getMyQueue: () => API.get('/api/delta/my-queue/').then((r) => r.data),
  versionAction: (id, action, note) =>
    API.post(`/api/delta/versions/${id}/${action}/`, { note: note || '' }).then((r) => r.data),
  getVersionComments: (id) =>
    API.get(`/api/delta/versions/${id}/comments/`).then((r) => r.data),
  addVersionComment: (id, { body, audio, seconds }) => {
    const form = new FormData();
    if (body) form.append('body', body);
    if (audio) form.append('audio', audio, audio.name);
    if (seconds != null) form.append('audio_seconds', String(seconds));
    return API.post(`/api/delta/versions/${id}/comments/`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  getDocuments: (params = {}) => API.get('/api/delta/documents/', { params }).then((r) => r.data),
  downloadDocument: (id) => API.get(`/api/delta/documents/${id}/download/`).then((r) => r.data),
};

export default deltaService;
